import React from 'react';
import { Target, Eye } from 'lucide-react';
import { useI18n } from '../context/I18nContext';

/**
 * Single refactored info card. Typography tightened:
 *  - Title uses tracking-tight and stronger weight so it reads as a real
 *    heading even against dense body copy.
 *  - Body lifted from text-gray-500 → text-gray-600 for better legibility
 *    on the #f7f7f9 section background (gray-500 fails WCAG on this BG).
 *  - Leading reduced from 1.9 → 1.7 to keep the block compact and
 *    comfortable on both desktop and iPad portrait.
 */
function InfoCard({ Icon, iconGradient, title, body }) {
  return (
    <div
      className="bg-white rounded-2xl p-8 md:p-10 shadow-[0_10px_30px_rgba(0,0,0,0.05)] hover:shadow-[0_18px_45px_rgba(0,0,0,0.08)] transition-shadow duration-300"
      data-testid="about-info-card"
    >
      <div className="flex items-center gap-5 mb-6">
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0 shadow-md"
          style={{ background: iconGradient }}
        >
          <Icon className="w-7 h-7 text-white" strokeWidth={2.2} />
        </div>
        <h3 className="text-xl md:text-[22px] font-bold tracking-tight text-gray-900">
          {title}
        </h3>
      </div>
      <p className="text-gray-600 text-[16px] md:text-[17px] leading-[1.7]">{body}</p>
    </div>
  );
}

export default function AboutSection() {
  const { t } = useI18n();
  return (
    <section id="about" className="py-16 md:py-24 bg-[#f7f7f9]">
      <div className="zh-container">
        {/* Title + eyebrow underline. Removed `uppercase tracking-wide` — the
            classes pushed letter-spacing so far apart that the title felt
            corporate/generic. `tracking-tight` mirrors the rest of the site's
            display headings and is more "Apple-like" / modern. */}
        <div className="mb-10 md:mb-12">
          <div className="inline-block text-[12px] md:text-[13px] font-bold uppercase tracking-[0.22em] text-[#f5a623] mb-4">
            About
          </div>
          <h2 className="text-[40px] md:text-6xl font-extrabold tracking-tight text-gray-900 leading-[1.05] mb-5">
            {t.about.title}
          </h2>
          <div className="h-1.5 w-24 rounded-full bg-[#f5a623]" />
        </div>

        {/* Intro body — tighter max-width for more comfortable line length
            (55–75 chars is the sweet spot). text-gray-600 for contrast. */}
        <p className="text-gray-600 text-lg md:text-xl leading-[1.7] max-w-3xl mb-14 md:mb-16">
          {t.about.body}
        </p>

        {/* Cards */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          <InfoCard
            Icon={Target}
            iconGradient="linear-gradient(135deg, #ff8a3d 0%, #f5a623 100%)"
            title={t.about.mission_title}
            body={t.about.mission_body}
          />
          <InfoCard
            Icon={Eye}
            iconGradient="linear-gradient(135deg, #34d17c 0%, #10b981 100%)"
            title={t.about.vision_title}
            body={t.about.vision_body}
          />
        </div>
      </div>
    </section>
  );
}
